<!-- eslint-disable vue/attributes-order -->
<script>
import JiraAPI from 'dashboard/api/integrations/jira';
import { useAlert } from 'dashboard/composables';
import { isValidURL } from 'dashboard/helper/URLHelper';
import { useVuelidate } from '@vuelidate/core';

import Spinner from 'shared/components/Spinner.vue';

export default {
  components: { Spinner },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isLoading: false,
      isFetchingProjects: false,
      isFetchingIssueTypes: false,
      isCreatingIntegration: false,

      accountHasAuthentication: false,

      domain: '',
      accessToken: '',
      templateMessage: '',
      projectName: '',
      projectId: null,

      fetchedProjects: [],
      alreadyFetchedProjects: false,
      selectedProject: null,

      fetchedIssueTypes: [],
      alreadyFetchedIssueTypes: false,
      selectedIssueTypes: [],

      alreadyFetchedIntegrationData: false,

      showDeleteModal: false,
    };
  },
  validations() {
    const validations = {
      domain: {
        validUrl(value) {
          if (!value) return true; // Skip msg error if URL is missing
          return isValidURL(value);
        },
      },
    };

    return validations;
  },
  computed: {
    fetchedData() {
      const { isLoading, projectId, domain, alreadyFetchedIntegrationData } =
        this;
      return !isLoading && projectId && domain && alreadyFetchedIntegrationData;
    },
  },
  mounted() {
    this.fetchIntegrationData();
  },
  methods: {
    async fetchIntegrationData() {
      this.isLoading = true;
      try {
        const { data } = await JiraAPI.get();
        this.domain = data.domain;
        this.templateMessage = data.template_message;
        this.projectId = data.project.id;
        this.projectName = data.project.name;
        this.alreadyFetchedIntegrationData = true;
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.JIRA.FETCH_INTEGRATION.ERROR_MESSAGE')
        );
      } finally {
        this.isLoading = false;
      }
    },
    async removeIntegration() {
      this.showDeleteModal = false;
      this.isLoading = true;
      try {
        await JiraAPI.delete();
        this.domain = '';
        this.accessToken = '';
        this.resetProject();
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.REMOVE_INTEGRATION.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.JIRA.REMOVE_INTEGRATION.ERROR_MESSAGE')
        );
      } finally {
        this.isLoading = false;
      }
    },
    async fetchProjects() {
      this.isFetchingProjects = true;
      try {
        const { data } = await JiraAPI.fetchProjects(
          this.domain,
          this.accessToken
        );
        if (Array.isArray(data)) this.fetchedProjects = data;
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.JIRA.FETCH_PROJECTS.ERROR_MESSAGE')
        );
      } finally {
        this.isFetchingProjects = false;
        this.alreadyFetchedProjects = true;
        if (!this.fetchedProjects.length)
          useAlert(
            this.$t('INTEGRATION_SETTINGS.JIRA.FETCH_PROJECTS.ERROR_MESSAGE')
          );
      }
    },
    async fetchIssueTypes() {
      if (!this.selectedProject?.id) return;

      this.isFetchingIssueTypes = true;
      try {
        const { data } = await JiraAPI.firstFetchIssueTypes(
          this.domain,
          this.accessToken,
          this.selectedProject.id
        );
        if (Array.isArray(data)) this.fetchedIssueTypes = data;
        if (!this.fetchedIssueTypes.length)
          useAlert(
            this.$t(
              'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.FIRST_FETCH_ERROR'
            )
          );
      } catch (error) {
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.FIRST_FETCH_ERROR'
          )
        );
      } finally {
        this.isFetchingIssueTypes = false;
        this.alreadyFetchedIssueTypes = true;
      }
    },
    async createIntegration() {
      this.isCreatingIntegration = true;
      try {
        const payload = {
          domain: this.domain,
          access_token: this.accessToken,
          template_message: this.templateMessage,
          project: {
            id: this.projectId,
            name: this.projectName,
          },
          permitted_issue_types_ids: this.selectedIssueTypes.map(e => e.id),
        };
        const { data } = await JiraAPI.create(payload);
        this.domain = data.domain;
        this.projectId = data.project.id;
        this.projectName = data.project.name;
        this.alreadyFetchedIntegrationData = true;
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_INTEGRATION.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.JIRA.CREATE_INTEGRATION.ERROR_MESSAGE')
        );
      } finally {
        this.isCreatingIntegration = false;
      }
    },
    changeProjectOption() {
      if (!this.selectedProject?.id) return;
      const { id, name } = this.selectedProject;
      this.projectId = id;
      this.projectName = name;
      this.fetchedIssueTypes = [];
      this.alreadyFetchedIssueTypes = false;
      this.selectedIssueTypes = [];
      this.fetchIssueTypes();
    },
    resetProject() {
      this.projectId = null;
      this.projectName = '';
      this.fetchedProjects = [];
      this.alreadyFetchedProjects = false;
      this.selectedProject = null;
      this.alreadyFetchedIntegrationData = false;
      this.templateMessage = '';
      this.fetchedIssueTypes = [];
      this.alreadyFetchedIssueTypes = false;
      this.selectedIssueTypes = [];
    },
  },
};
</script>

<template>
  <div>
    <h4
      class="block-title my-0 mx-2 text-2xl text-slate-800 dark:text-slate-100"
    >
      Jira
    </h4>
    <Spinner v-if="isLoading" color-scheme="primary" />
    <div v-else class="mx-2 mt-2 max-w-[30rem]">
      <div
        class="py-2 px-4 border border-solid dark:border-slate-500 border-slate-200 rounded-md"
      >
        <!-- START DOMAIN SECTION -->
        <div>
          <label>
            {{ $t('INTEGRATION_SETTINGS.JIRA.DOMAIN.LABEL') }}
          </label>
          <span v-if="fetchedData">{{ domain }}</span>
          <woot-input
            v-else
            v-model.trim="domain"
            :placeholder="$t('INTEGRATION_SETTINGS.JIRA.DOMAIN.PLACEHOLDER')"
            @input="v$.domain.$touch"
          />
          <span v-if="!v$.domain.validUrl" class="text-red-400">
            {{ $t('INTEGRATION_SETTINGS.JIRA.DOMAIN.INVALID_INPUT_TEXT') }}
          </span>
        </div>
        <!-- END DOMAIN SECTION -->

        <!-- START ACCESS TOKEN SECTION -->
        <div v-if="!fetchedData">
          <label>
            {{ $t('INTEGRATION_SETTINGS.JIRA.ACCESS_TOKEN.LABEL') }}
          </label>
          <woot-input
            v-model.trim="accessToken"
            :placeholder="
              $t('INTEGRATION_SETTINGS.JIRA.ACCESS_TOKEN.PLACEHOLDER')
            "
            @input="resetProject"
          />
        </div>
        <!-- END ACCESS TOKEN SECTION -->

        <!-- START PROJECT SELECTOR SECTION -->
        <div>
          <div v-if="fetchedData">
            <label>
              {{ $t('INTEGRATION_SETTINGS.JIRA.PROJECT.LABEL') }}
            </label>
            <span>{{ projectName }}</span>
          </div>

          <div v-else>
            <div v-if="alreadyFetchedProjects && !!fetchedProjects.length">
              <label>
                {{ $t('INTEGRATION_SETTINGS.JIRA.PROJECT.SELECT_LABEL') }}
              </label>
              <multiselect
                v-model="selectedProject"
                :options="fetchedProjects"
                track-by="id"
                label="name"
                :placeholder="
                  $t('INTEGRATION_SETTINGS.JIRA.PROJECT.PLACEHOLDER')
                "
                selected-label
                :max-height="300"
                :allow-empty="false"
                :option-height="104"
                @select="changeProjectOption"
              />
            </div>
          </div>
        </div>
        <!-- END PROJECT SELECTOR SECTION -->

        <!-- START ISSUE TYPES SELECTOR SECTION -->
        <div
          v-if="
            !fetchedData &&
            !!selectedProject &&
            alreadyFetchedProjects &&
            !!fetchedProjects.length
          "
        >
          <label>
            {{ $t('INTEGRATION_SETTINGS.JIRA.ISSUE_TYPES.SELECT_LABEL') }}
          </label>
          <Spinner v-if="isFetchingIssueTypes" color-scheme="primary" />
          <span
            v-else-if="alreadyFetchedIssueTypes && !fetchedIssueTypes.length"
            class="text-red-400 text-sm"
          >
            {{
              $t(
                'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.FIRST_FETCH_ERROR'
              )
            }}
          </span>
          <multiselect
            v-else
            v-model="selectedIssueTypes"
            :options="fetchedIssueTypes"
            track-by="id"
            label="name"
            :placeholder="
              $t('INTEGRATION_SETTINGS.JIRA.ISSUE_TYPES.PLACEHOLDER')
            "
            :max-height="300"
            multiple
            :allow-empty="false"
            :option-height="104"
          >
            <!-- Whats is displayed when selecting (list is open) -->
            <template #option="{ option }">
              <div class="flex gap-2 align-center">
                <img
                  class="w-5 h-5"
                  :src="option.icon_url"
                  :alt="option.name"
                />
                <span>{{ option.name }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <!-- END ISSUE TYPES SELECTOR SECTION -->

        <!-- START TEMPLATE MESSAGE SECTION -->
        <div v-if="projectId || !!fetchedProjects.length">
          <label>
            {{ $t('INTEGRATION_SETTINGS.JIRA.TEMPLATE_MESSAGE.LABEL') }}
          </label>
          <div v-if="fetchedData">
            <p v-if="!templateMessage">-</p>
            <textarea
              v-else
              v-model="templateMessage"
              class="min-h-[8rem] border-0 text-sm bg-slate-100 dark:bg-slate-700"
              rows="4"
              readonly
            />
          </div>
          <textarea
            v-else
            v-model="templateMessage"
            class="min-h-[8rem] text-sm"
            rows="4"
            :readonly="fetchedData"
            :placeholder="
              $t('INTEGRATION_SETTINGS.JIRA.TEMPLATE_MESSAGE.PLACEHOLDER')
            "
          />
        </div>
        <!-- END TEMPLATE MESSAGE SECTION -->
      </div>

      <div class="mt-3">
        <div v-if="!fetchedData" class="flex gap-2">
          <woot-button
            v-if="!(alreadyFetchedProjects && fetchedProjects.length)"
            class="w-[50%] justify-center"
            icon="search"
            :disabled="!(domain && accessToken && v$.domain.validUrl)"
            :is-loading="isFetchingProjects"
            @click="fetchProjects"
          >
            {{ $t('INTEGRATION_SETTINGS.JIRA.FETCH_PROJECTS.BUTTON_TEXT') }}
          </woot-button>

          <woot-button
            class="w-full justify-center"
            :disabled="
              !(domain && accessToken && projectId && selectedIssueTypes.length)
            "
            icon="save"
            color-scheme="success"
            :is-loading="isCreatingIntegration"
            @click="createIntegration"
          >
            {{ $t('INTEGRATION_SETTINGS.JIRA.CREATE_INTEGRATION.BUTTON_TEXT') }}
          </woot-button>
        </div>
        <div v-else>
          <woot-button
            class="w-full justify-center"
            icon="delete"
            color-scheme="alert"
            @click="showDeleteModal = true"
          >
            {{ $t('INTEGRATION_SETTINGS.JIRA.REMOVE_INTEGRATION.BUTTON_TEXT') }}
          </woot-button>
          <p class="text-xs pt-1">
            {{
              $t(
                'INTEGRATION_SETTINGS.JIRA.REMOVE_INTEGRATION.OBSERVATION_MESSAGE'
              )
            }}
          </p>
        </div>
      </div>
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="removeIntegration"
      :title="$t('INTEGRATION_SETTINGS.JIRA.REMOVE_INTEGRATION.CONFIRM.TITLE')"
      :message="
        $t('INTEGRATION_SETTINGS.JIRA.REMOVE_INTEGRATION.CONFIRM.MESSAGE')
      "
      :confirm-text="
        $t('INTEGRATION_SETTINGS.JIRA.REMOVE_INTEGRATION.CONFIRM.OPTIONS.YES')
      "
      :reject-text="
        $t('INTEGRATION_SETTINGS.JIRA.REMOVE_INTEGRATION.CONFIRM.OPTIONS.NO')
      "
    />
  </div>
</template>
